import { api, uploadFile } from './api';
import { env } from '../utils/env';

// Configuração base da API
const API_BASE_URL = env.API_URL;

export interface AvatarUploadResponse {
  success: boolean;
  message: string;
  data?: {
    avatarUrl: string;
    filename: string;
  };
  errors?: string[];
}

class AvatarService {
  /**
   * Faz upload do avatar do usuário
   */
  async uploadAvatar(
    imageBlob: Blob, 
    onProgress?: (progress: number) => void
  ): Promise<AvatarUploadResponse> {
    try {
      // Criar um arquivo a partir do blob
      const file = new File([imageBlob], `avatar-${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });

      // Fazer upload do arquivo
      const response = await uploadFile('/auth/avatar', file, onProgress);
      
      // A resposta já vem processada pelo interceptor
      return response as unknown as AvatarUploadResponse;
    } catch (error: any) {
      console.error('Erro ao fazer upload do avatar:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao fazer upload do avatar',
        errors: error.response?.data?.errors || ['Erro interno do servidor'],
      };
    }
  }

  /**
   * Remove o avatar do usuário
   */
  async removeAvatar(): Promise<AvatarUploadResponse> {
    try {
      const response = await api.delete('/auth/avatar');
      
      // A resposta já vem processada pelo interceptor
      return response as unknown as AvatarUploadResponse;
    } catch (error: any) {
      console.error('Erro ao remover avatar:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao remover avatar',
        errors: error.response?.data?.errors || ['Erro interno do servidor'],
      };
    }
  }

  /**
   * Obtém a URL completa do avatar
   */
  getAvatarUrl(avatarPath?: string): string {
    if (!avatarPath) {
      return '';
    }

    // Se já é uma URL completa, retorna como está
    if (avatarPath.startsWith('http')) {
      return avatarPath;
    }

    // Constrói a URL completa baseada no backend
    const baseUrl = API_BASE_URL;
    return `${baseUrl}/uploads/avatars/${avatarPath}`;
  }

  /**
   * Gera iniciais do nome para usar como fallback
   */
  getInitials(name: string): string {
    if (!name) return '?';
    
    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  /**
   * Valida se o arquivo é uma imagem válida
   */
  validateImageFile(file: File): { valid: boolean; error?: string } {
    // Verificar tipo
    if (!file.type.startsWith('image/')) {
      return {
        valid: false,
        error: 'Arquivo deve ser uma imagem (JPG, PNG, GIF, WebP)',
      };
    }

    // Verificar tamanho (máximo 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Arquivo deve ter no máximo 10MB',
      };
    }

    // Verificar formatos aceitos
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Formato não suportado. Use JPG, PNG, GIF ou WebP',
      };
    }

    return { valid: true };
  }

  /**
   * Redimensiona uma imagem para as dimensões especificadas
   */
  async resizeImage(
    file: File, 
    maxWidth: number = 400, 
    maxHeight: number = 400, 
    quality: number = 0.9
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      if (!ctx) {
        reject(new Error('Não foi possível criar contexto do canvas'));
        return;
      }

      img.onload = () => {
        // Calcular novas dimensões mantendo proporção
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Configurar canvas
        canvas.width = width;
        canvas.height = height;

        // Desenhar imagem redimensionada
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Converter para blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Erro ao processar imagem'));
            }
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('Erro ao carregar imagem'));
      };

      // Carregar imagem
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  }
}

export default new AvatarService(); 